import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { DocumentDto } from '@lawyer/shared'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { formatBytes } from '../../lib/format'
import { ErrorAlert } from '../common/Feedback'
import { FileDropZone } from './FileDropZone'

export type UploadScope = {
  caseId?: string
  clientId?: string
  taskId?: string
}

type Props = {
  open: boolean
  scope: UploadScope
  /** Optional client picker, used on the case documents panel. */
  clientChoices?: { id: string; label: string }[]
  /** Set by the one-click shortcuts, which name the document before the file is picked. */
  defaultName?: string
  /** A file already chosen outside the dialog, by a shortcut. */
  defaultFile?: File | null
  onClose: () => void
  onUploaded: () => void
}

export function UploadDocumentDialog({
  open,
  scope,
  clientChoices,
  defaultName = '',
  defaultFile = null,
  onClose,
  onUploaded,
}: Props) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [clientId, setClientId] = useState('')

  useEffect(() => {
    if (!open) return
    setFile(defaultFile)
    setName(defaultName || defaultFile?.name || '')
    setDescription('')
    setClientId(scope.clientId ?? '')
    if (fileInput.current) fileInput.current.value = ''
  }, [open, scope.clientId, defaultName, defaultFile])

  const pickFile = (selected: File) => {
    setFile(selected)
    if (!name) setName(selected.name)
  }

  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('לא נבחר קובץ')
      const formData = new FormData()
      formData.append('file', file)
      if (name.trim()) formData.append('name', name.trim())
      if (description.trim()) formData.append('description', description.trim())
      if (scope.caseId) formData.append('caseId', scope.caseId)
      if (scope.taskId) formData.append('taskId', scope.taskId)
      if (clientId) formData.append('clientId', clientId)
      return api.upload<DocumentDto>('/documents', formData)
    },
    onSuccess: () => {
      onUploaded()
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>העלאת מסמך</DialogTitle>

      <DialogContent dividers>
        {upload.error ? <ErrorAlert error={upload.error} /> : null}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <FileDropZone onFile={pickFile} overlayText="שחררו את הקובץ כאן">
            <Button
              component="label"
              variant="outlined"
              fullWidth
              sx={{ py: 3, borderStyle: 'dashed', flexDirection: 'column' }}
            >
              {file ? 'בחירת קובץ אחר' : 'בחירת קובץ'}
              <Typography variant="caption" color="text.secondary">
                או גררו קובץ לכאן
              </Typography>
              <input
                ref={fileInput}
                type="file"
                hidden
                onChange={(e) => {
                  const selected = e.target.files?.[0]
                  if (selected) pickFile(selected)
                }}
              />
            </Button>
          </FileDropZone>

          {file ? (
            <Typography variant="body2" color="text.secondary">
              {file.name} · {formatBytes(file.size)}
            </Typography>
          ) : null}

          <TextField label="שם תצוגה" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="תיאור"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {clientChoices ? (
            <TextField
              select
              label="שיוך ללקוח בתיק"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              helperText="ריק = מסמך של התיק כולו"
            >
              <MenuItem value="">מסמך של התיק</MenuItem>
              {clientChoices.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          variant="contained"
          onClick={() => upload.mutate()}
          disabled={!file || upload.isPending}
        >
          {upload.isPending ? 'מעלה…' : 'העלאה'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
